/**
 * `?sample=1`, and why a browser app has a harness at all.
 *
 * The desktop app has had one since its first week — `RE_SAMPLE`, `RE_NO_AUTH`
 * and the `RE_SHOT` grabs in `relay/CLAUDE.md` — and it exists because a
 * surface cannot be looked at if reaching it needs an account, a server and
 * somebody to talk to. This is the same idea with the same shape: the sample
 * store, and the sign-in gate stepped over, so every screen can be opened and
 * compared against the app it is meant to look like.
 *
 * **It cannot exist in a build.** `import.meta.env.DEV` is replaced by `false`
 * when Vite builds, so the whole condition folds away and the query string
 * means nothing in anything that ships. That is the only thing standing
 * between a development convenience and a way past the sign-in gate, so it is
 * checked here, once, and nowhere else.
 */
export function sampleMode(): boolean {
  if (!import.meta.env.DEV) return false;
  // `window` without a `location` is a real shape, not a defensive guess: the
  // tests run in Vitest's node environment, where the global exists and that
  // property does not. `store.ts` calls this while the module is being
  // evaluated, so anything thrown here fails a whole test file on import.
  const search = typeof window === 'undefined' ? undefined : window.location?.search;
  if (!search) return false;
  return new URLSearchParams(search).get('sample') === '1';
}
